import { useEffect } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { api, session } from '../lib/api'
import { useClock, useConnection, useOutboxCount } from '../lib/hooks'
import { IconHome, IconQueue, IconScale, IconCog, IconBox, IconTruck, IconStore } from '../ui/kit'
import { Today } from './Today'
import { WeighPay } from './WeighPay'
import { Queue } from './Queue'
import { FieldLot } from './FieldLot'
import { More } from './More'
import { DepotIn } from './DepotIn'
import { MyRun } from './MyRun'
import { Packing } from './Packing'

export function FieldShell({ onSignOut }: { onSignOut: () => void }) {
  const nav = useNavigate()
  const loc = useLocation()
  const now = useClock()
  const { online } = useConnection()
  const queued = useOutboxCount()

  useEffect(() => {
    // The field app is always comfortable density — 44px targets, big type, one hand.
    document.documentElement.setAttribute('data-density', 'comfortable')
    return () => document.documentElement.setAttribute('data-density', 'compact')
  }, [])

  async function signOut() {
    if (queued > 0) {
      const ok = window.confirm(
        `${queued} item(s) have not reached the server yet. Signing out keeps them on this device, ` +
          'but nobody else can send them. Sign out anyway?',
      )
      if (!ok) return
    }
    await api.logout()
    onSignOut()
    // Back to the field door rather than the chooser: whoever picks this phone up
    // next is almost certainly another agent, not another kind of user.
    nav('/login/field', { replace: true })
  }

  const isWizard = loc.pathname.includes('/weigh')

  // The field app is three jobs in one shell. Which tabs you get follows the roles on
  // your account: an agent collects, a clerk weighs in at the depot, a driver delivers.
  // Someone holding two of those sees both.
  const collects = session.has('field_agent', 'admin', 'ops')
  const weighsIn = session.has('depot_clerk', 'admin', 'ops')
  const drives = session.has('driver', 'admin', 'ops')

  // Whoever only drives should land on the run, not on a collection route they have none of.
  const home = collects ? '/m' : drives ? '/m/run' : weighsIn ? '/m/depot' : '/m/more'

  return (
    <div className="field-shell">
      <div className="field-status">
        <span>{now.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
        <span className="row" style={{ gap: 6 }}>
          {!online && <span style={{ fontSize: 10, letterSpacing: '.06em' }}>OFFLINE</span>}
          <Signal online={online} />
          <Battery />
        </span>
      </div>

      <Routes>
        <Route index element={collects ? <Today /> : <Navigate to={home} replace />} />
        <Route path="weigh/:stopId" element={<WeighPay />} />
        <Route path="queue" element={<Queue />} />
        <Route path="lot/:code" element={<FieldLot />} />
        <Route path="more" element={<More onSignOut={signOut} />} />
        <Route path="depot" element={<DepotIn />} />
        <Route path="run" element={<MyRun />} />
        <Route path="pack" element={<Packing />} />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>

      {!isWizard && (
        <nav className="tabbar">
          {collects && <Tab to="/m" label="Today" icon={<IconHome size={19} />} end />}
          {weighsIn && <Tab to="/m/depot" label="Weigh in" icon={<IconBox size={19} />} />}
          {weighsIn && <Tab to="/m/pack" label="To pack" icon={<IconStore size={19} />} />}
          {drives && <Tab to="/m/run" label="My run" icon={<IconTruck size={19} />} />}
          <Tab to="/m/queue" label="To send" icon={<IconQueue size={19} />} badge={queued} />
          <Tab to="/m/lot/search" label="Collections" icon={<IconScale size={19} />} />
          <Tab to="/m/more" label="More" icon={<IconCog size={19} />} />
        </nav>
      )}
    </div>
  )
}

function Tab({ to, label, icon, badge, end }: {
  to: string; label: string; icon: JSX.Element; badge?: number; end?: boolean
}) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}>
      {icon}
      <span>{label}</span>
      {badge ? <span className="tab-badge">{badge}</span> : null}
    </NavLink>
  )
}

function Signal({ online }: { online: boolean }) {
  return (
    <svg width="15" height="11" viewBox="0 0 15 11" aria-label={online ? 'Online' : 'Offline'}>
      {online ? (
        <path d="M1 8.5h3v2H1zM5 6h3v4.5H5zM9 3.5h3V10.5H9zM13 1h2v9.5h-2z" fill="currentColor" />
      ) : (
        <>
          <path d="M1 8.5h3v2H1z" fill="currentColor" opacity=".45" />
          <path d="M1 1l13 9" stroke="currentColor" strokeWidth="1.4" />
        </>
      )}
    </svg>
  )
}

function Battery() {
  return (
    <svg width="16" height="11" viewBox="0 0 16 11" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="11" height="9" rx="2" stroke="currentColor" />
      <rect x="2.5" y="2.5" width="7" height="6" rx="1" fill="currentColor" />
      <path d="M14 4v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

export function FieldHeader({ title, back, right }: {
  title?: string
  back?: () => void
  right?: JSX.Element
}) {
  const user = session.user
  return (
    <header className="field-head">
      {back ? (
        <button className="back" onClick={back}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round"><path d="M15 5l-7 7 7 7" /></svg>
          {title ?? 'Back'}
        </button>
      ) : (
        <div className="field-brand">
          <span className="mark">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff"
              strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 22V9M12 9c0-4 3-6 8-6 0 5-2.5 8-8 8z" />
              <path d="M12 13c0-3-2-5-6-5 0 4 1.8 6.4 6 6.6z" opacity=".75" />
            </svg>
          </span>
          Ledger
        </div>
      )}
      {right ?? (
        <span className="pill" style={{ textTransform: 'none' }}>
          {user?.roles[0]?.replace(/_/g, ' ') ?? ''}
        </span>
      )}
    </header>
  )
}
